import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import moment from "moment";
import { useTranslation } from "react-i18next";
import Spinner from "components/common/Spinner";
import UserDateTimeProvider from "contexts/UserDateTimeProvider";
import { Col, Row } from "components/bootstrap";
import useRoutingQuery from "routing/useQuery";
import { timeRangeFromQueryParameter } from "views/logic/TimeRange";
import AutoRefreshDisabledProvider from "report/common/AutoRefreshDisabledProvider";
import useParams from "routing/useParams";
import _deserializeWidgetConfig from "common/utils/deserializeWidgetConfig";
import ExportingWidget from "search/ExportWidgetAction/pdf-export-widget-renderer/ExportingWidget";
import { REPORT_TZ_FALLBACK } from "report/Constants";
import { RenderCompletionObserver } from "components/visualizations";
import useExportWidget from "search/ExportWidgetAction/pdf-export-widget-renderer/hooks/useExportWidget";
import PageContextProviders from "components/page/contexts/PageContextProviders";
import GlobalPDFStyles from "report/common/GlobalPDFStyles";
import PDFExportThemeProvider from "common/providers/PDFExportThemeProvider";
import ExportErrorBoundary from "export/ExportErrorBoundary";
import { getExportWidgetSizes } from "search/ExportWidgetAction/util/WidgetExportUtils";
const CoverPage = styled.div`
  padding-top: 30%;

  h1 {
    font-size: 2em;
    text-align: center;
    margin-bottom: 0.8em;
  }

  h2 {
    color: #555 !important;
    font-size: 1.6em;
    text-align: center;
  }
`;
export const Description = styled.p`
  white-space: pre-wrap;
`;
const InvisibleDiv = styled.div`
  display: none;
`;
const Container = styled.div`
  margin-top: 10px;
  margin-bottom: 30px;
`;
const extractTimeRangeOverride = (query) => "rangetype" in query ? timeRangeFromQueryParameter(query) : void 0;
const referenceTimeFromQueryParameter = (query) => moment(query.now);
const extractNow = (query) => "now" in query ? referenceTimeFromQueryParameter(query) : void 0;
const RenderedWidget = () => {
  var _a, _b, _c, _d;
  const { t } = useTranslation("enterprise_search");
  const query = useRoutingQuery();
  const [isRenderComplete, setIsRenderComplete] = useState(false);
  const { exportCallId } = useParams();
  const timerangeOverride = useMemo(() => extractTimeRangeOverride(query), [query]);
  const now = useMemo(() => extractNow(query), [query]);
  const { isFetching, widgetData, widgetValues } = useExportWidget(exportCallId, timerangeOverride, now);
  const reportTz = (_a = widgetData == null ? void 0 : widgetData.timezone) != null ? _a : REPORT_TZ_FALLBACK;
  const deserializedWidget = widgetData && {
    ...widgetData,
    config: _deserializeWidgetConfig(widgetData)
  };
  const isDataLoaded = !!widgetData && !!(widgetValues == null ? void 0 : widgetValues.result) && !isFetching;
  const _orientation = (_b = widgetData == null ? void 0 : widgetData.orientation) != null ? _b : "portrait";
  const handleRenderComplete = useCallback(() => {
    setIsRenderComplete(true);
  }, []);
  const { width, height } = getExportWidgetSizes(
    (_c = widgetData == null ? void 0 : widgetData.position) == null ? void 0 : _c.height,
    (_d = widgetData == null ? void 0 : widgetData.position) == null ? void 0 : _d.width,
    _orientation
  );
  return isDataLoaded ? /* @__PURE__ */ React.createElement(PageContextProviders, null, /* @__PURE__ */ React.createElement(AutoRefreshDisabledProvider, null, /* @__PURE__ */ React.createElement(UserDateTimeProvider, { tz: reportTz }, /* @__PURE__ */ React.createElement(GlobalPDFStyles, { $orientation: _orientation }), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12 }, /* @__PURE__ */ React.createElement(CoverPage, null, /* @__PURE__ */ React.createElement("p", { className: "text-center" }, t("rendered_widget.timezone_note", { timezone: reportTz }))), /* @__PURE__ */ React.createElement(Container, null, /* @__PURE__ */ React.createElement(RenderCompletionObserver, { onRenderComplete: handleRenderComplete }, /* @__PURE__ */ React.createElement(ExportingWidget, { widget: deserializedWidget, value: widgetValues, height, width }))), /* @__PURE__ */ React.createElement(InvisibleDiv, { id: "render-started" }), isRenderComplete && /* @__PURE__ */ React.createElement(InvisibleDiv, { id: "render-complete" })))))) : /* @__PURE__ */ React.createElement(Spinner, null);
};
const ExportWidgetPage = () => /* @__PURE__ */ React.createElement(ExportErrorBoundary, null, /* @__PURE__ */ React.createElement(PDFExportThemeProvider, null, /* @__PURE__ */ React.createElement(RenderedWidget, null)));
export default ExportWidgetPage;
